import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname.startsWith('/admin') && !window.location.pathname.includes('/login')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
};

// Users
export const usersAPI = {
  getAll: (role) => api.get('/users', { params: { role } }),
  getById: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.patch(`/users/${id}`, data),
};

// Driver Availability
export const driversAPI = {
  getAvailability: (driverId) => api.get(`/drivers/${driverId}/availability`),
  updateAvailability: (driverId, dates) => api.put(`/drivers/${driverId}/availability`, { unavailable_dates: dates }),
  addUnavailableDate: (driverId, date) => api.post(`/drivers/${driverId}/availability/add`, { date }),
  removeUnavailableDate: (driverId, date) => api.post(`/drivers/${driverId}/availability/remove`, { date }),
};

// Customers
export const customersAPI = {
  getAll: () => api.get('/customers'),
  getById: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.patch(`/customers/${id}`, data),
};

// Admin
export const adminAPI = {
  resetDemoData: () => api.post('/admin/reset-demo-data'),
};

// Units
export const unitsAPI = {
  getAll: (params) => api.get('/units', { params }),
  getOverdue: () => api.get('/units/overdue'),
  getById: (id) => api.get(`/units/${id}`),
  create: (data) => api.post('/units', data),
  update: (id, data) => api.patch(`/units/${id}`, data),
};

// Routes
export const routesAPI = {
  getAll: (date) => api.get('/routes', { params: { date } }),
  getById: (id) => api.get(`/routes/${id}`),
  create: (data) => api.post('/routes', data),
  update: (id, data) => api.patch(`/routes/${id}`, data),
  getRecommendations: (date) => api.get(`/routes/recommendations/${date}`),
  acceptRecommendation: (recommendation) => api.post('/routes/recommendations/accept', recommendation),
  autoGenerate: (date) => api.post(`/routes/auto-generate/${date}`),
};

// Weekly Schedule
export const scheduleAPI = {
  getWeekly: (startDate) => api.get('/schedule/weekly', { params: { start_date: startDate } }),
};

// Jobs
export const jobsAPI = {
  getAll: (params) => api.get('/jobs', { params }),
  getById: (id) => api.get(`/jobs/${id}`),
  create: (data) => api.post('/jobs', data),
  update: (id, data) => api.patch(`/jobs/${id}`, data),
  reorder: (orders) => api.post('/jobs/reorder', orders),
  unassignFromRoute: (id) => api.post(`/jobs/${id}/unassign-route`),
  getAvailableUnits: (id) => api.get(`/jobs/${id}/available-units`),
  assignUnits: (id, unitIds) => api.post(`/jobs/${id}/assign-units`, { unit_ids: unitIds }),
  unassignUnits: (id, unitIds) => api.post(`/jobs/${id}/unassign-units`, { unit_ids: unitIds }),
  extendRental: (id, data) => api.post(`/jobs/${id}/extend-rental`, data),
  endRentalEarly: (id, data) => api.post(`/jobs/${id}/end-rental-early`, data),
};

// Service Logs
export const serviceLogsAPI = {
  getAll: (params) => api.get('/service-logs', { params }),
  create: (data) => api.post('/service-logs', data),
};

// Issue Reports
export const issueReportsAPI = {
  getAll: (params) => api.get('/issue-reports', { params }),
  getById: (id) => api.get(`/issue-reports/${id}`),
  create: (data) => api.post('/issue-reports', data),
  update: (id, data) => api.patch(`/issue-reports/${id}`, data),
};

// Notifications
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
};

// Invoices
export const invoicesAPI = {
  getAll: (params) => api.get('/invoices', { params }),
  getById: (id) => api.get(`/invoices/${id}`),
  getStats: () => api.get('/invoices/stats'),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.patch(`/invoices/${id}`, data),
  send: (id) => api.post(`/invoices/${id}/send`),
  markPaid: (id) => api.post(`/invoices/${id}/mark-paid`),
  addLineItem: (invoiceId, data) => api.post(`/invoices/${invoiceId}/line-items`, data),
  updateLineItem: (invoiceId, lineItemId, data) => api.patch(`/invoices/${invoiceId}/line-items/${lineItemId}`, data),
  deleteLineItem: (invoiceId, lineItemId) => api.delete(`/invoices/${invoiceId}/line-items/${lineItemId}`),
};

// Photo Upload
export const uploadAPI = {
  uploadPhoto: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadBase64: (imageData) => api.post('/upload/photo-base64', { image: imageData }),
};

// Quote Requests
export const quoteRequestsAPI = {
  getAll: (status) => api.get('/quote-requests', { params: { status } }),
  getById: (id) => api.get(`/quote-requests/${id}`),
  create: (data) => api.post('/quote-requests', data),
  update: (id, data) => api.patch(`/quote-requests/${id}`, data),
  approve: (id, data) => api.post(`/quote-requests/${id}/approve`, data),
};

// CRM Leads
export const leadsAPI = {
  getAll: (params) => api.get('/leads', { params }),
  getById: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.patch(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  addCallLog: (id, data) => api.post(`/leads/${id}/call-log`, data),
  getStats: () => api.get('/leads/stats/summary'),
  bulkImport: (leads) => api.post('/leads/bulk', leads),
};

// Dashboard
export const dashboardAPI = {
  get: () => api.get('/dashboard'),
};

// Driver
export const driverAPI = {
  getTodayRoute: (driverId) => api.get(`/driver/${driverId}/today`),
};

// Portal (public)
export const portalAPI = {
  submitQuote: (data) => axios.post(`${API_BASE}/quote-requests`, data),
  getMyRentals: (customerId) => axios.get(`${API_BASE}/portal/my-rentals/${customerId}`),
  submitServiceRequest: (data) => axios.post(`${API_BASE}/portal/service-request`, data),
  getDemoCustomer: () => axios.get(`${API_BASE}/portal/demo-customer`),
};

export default api;
